import React from 'react';
import { Box, MenuItem, Checkbox, ListItemText } from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { UserFilter } from '../../../types/user.types';
import { Role } from '../../../types/role.types';
import { useUserFilters, UseUserFiltersProps } from './UserFilters.hooks';
import {
  FilterContainer,
  FilterRow,
  SecondaryFilterRow,
  StyledTextField,
  FilterCollapse,
} from './UserFilters.styles';
import {
  FilterToolbarGradientButton,
  FilterToolbarOutlinedButton,
} from '../../../styles/theme/filterToolbarButtons';
import { ListPageActiveFilters } from '../../common/list-page';

export interface UserFiltersProps extends Omit<UseUserFiltersProps, 'filters' | 'roles'> {
  filters: UserFilter;
  roles: Role[];
  /** Bloquea el buscador mientras el drawer de usuario está abierto (evita autofill del navegador). */
  lockedSearchTerm?: string | null;
  className?: string;
}

const UserFiltersComponent: React.FC<UserFiltersProps> = ({
  filters,
  setFilters,
  appliedFilters,
  onClearAppliedField,
  showMoreFilters,
  roles,
  lockedSearchTerm = null,
  onSearch,
  onReset,
  onToggleMoreFilters,
  className,
  onFilterChange,
}) => {
  const {
    handleInputChange,
    handleSelectChange,
    handleRoleSelectChange,
    handleKeyPress,
    handleDateChange,
    renderStatusValue,
    renderSortByValue,
    renderRoleValue,
    activeFilterChips,
  } = useUserFilters({
    filters,
    setFilters,
    appliedFilters,
    onClearAppliedField,
    showMoreFilters,
    roles,
    onSearch,
    onReset,
    onToggleMoreFilters,
    onFilterChange,
  });

  const isSearchLocked = lockedSearchTerm !== null;
  const searchDisplayValue = isSearchLocked ? lockedSearchTerm : filters.searchTerm || '';

  return (
    <FilterContainer className={className}>
      {/* Primera fila - Campo de búsqueda y botones */}
      <FilterRow>
        <Box sx={{ flex: 1, minWidth: 200 }}>
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Buscar Usuario"
            name="users-list-search"
            id="users-list-search"
            value={searchDisplayValue}
            onChange={(e) => {
              if (isSearchLocked) return;
              const value = e.target.value;
              setFilters((prev) => ({ ...prev, searchTerm: value }));
              onFilterChange?.({ searchTerm: value });
            }}
            onKeyDown={isSearchLocked ? undefined : handleKeyPress}
            placeholder="Buscar por nombre o teléfono"
            InputLabelProps={{ shrink: true }}
            disabled={isSearchLocked}
            autoComplete="off"
            inputProps={{
              autoComplete: 'off',
              'data-lpignore': 'true',
              'data-1p-ignore': 'true',
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />,
            }}
          />
        </Box>

        <FilterToolbarOutlinedButton
          variant="outlined"
          size="medium"
          onClick={onToggleMoreFilters}
          startIcon={showMoreFilters ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        >
          {showMoreFilters ? 'Menos' : 'Más'}
        </FilterToolbarOutlinedButton>

        <FilterToolbarOutlinedButton
          variant="outlined"
          size="medium"
          onClick={onReset}
          startIcon={<RefreshIcon />}
        >
          Reiniciar
        </FilterToolbarOutlinedButton>

        <FilterToolbarGradientButton
          variant="contained"
          size="medium"
          onClick={onSearch}
          startIcon={<SearchIcon />}
        >
          Buscar
        </FilterToolbarGradientButton>
      </FilterRow>

      {/* Filtros adicionales */}
      <FilterCollapse in={showMoreFilters} timeout={350} unmountOnExit>
        <SecondaryFilterRow>
          {/* Estado */}
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Estado"
            name="status"
            value={filters.status || 'all'}
            onChange={(event) => handleSelectChange(event as any)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, renderValue: renderStatusValue }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Activo</MenuItem>
            <MenuItem value="blocked">Bloqueado</MenuItem>
          </StyledTextField>

          {/* Roles */}
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Roles"
            name="roleIds"
            value={filters.roleIds || []}
            onChange={(event) => handleRoleSelectChange(event as any)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{
              multiple: true,
              displayEmpty: true,
              renderValue: renderRoleValue,
            }}
          >
            {roles.length === 0 && (
              <MenuItem value="" disabled>
                No hay roles disponibles
              </MenuItem>
            )}
            {roles.map((role) => {
              const isSelected = (filters.roleIds || []).includes(role.id);
              return (
                <MenuItem key={role.id} value={role.id}>
                  <Checkbox checked={isSelected} />
                  <ListItemText primary={role.name} />
                </MenuItem>
              );
            })}
          </StyledTextField>

          {/* Fecha inicio */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Fecha Inicio"
            name="startDate"
            type="date"
            value={filters.startDate || ''}
            onChange={(e) => handleDateChange('startDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                max: filters.endDate || undefined,
              },
            }}
          />

          {/* Fecha fin */}
          <StyledTextField
            fullWidth
            variant="outlined"
            size="medium"
            label="Fecha Fin"
            name="endDate"
            type="date"
            value={filters.endDate || ''}
            onChange={(e) => handleDateChange('endDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            InputProps={{
              inputProps: {
                min: filters.startDate || undefined,
              },
            }}
          />

          {/* Ordenar por */}
          <StyledTextField
            fullWidth
            select
            variant="outlined"
            size="medium"
            label="Ordenar por"
            name="sortBy"
            value={filters.sortBy || ''}
            onChange={(event) => handleSelectChange(event as any)}
            InputLabelProps={{ shrink: true }}
            SelectProps={{ displayEmpty: true, renderValue: renderSortByValue }}
          >
            <MenuItem value="">Sin ordenar</MenuItem>
            <MenuItem value="email">Correo</MenuItem>
            <MenuItem value="firstName">Nombre</MenuItem>
            <MenuItem value="lastName">Apellido</MenuItem>
            <MenuItem value="createdAt">Fecha de creación</MenuItem>
            <MenuItem value="updatedAt">Última actualización</MenuItem>
          </StyledTextField>
        </SecondaryFilterRow>
      </FilterCollapse>

      <ListPageActiveFilters chips={activeFilterChips} />
    </FilterContainer>
  );
};

export default React.memo(UserFiltersComponent);
